import React, { createContext, useContext, useState, useRef, useMemo } from 'react';
import type { FC, ReactNode } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { MediaSharedClassNames } from '@atlaskit/editor-common/styles';

import type { CommentsDataMap } from '@confluence/comments-data';
import {
	getNextMarkerRef,
	getNextUnreadThreadKey,
} from '@confluence/comments-util/entry-points/domUtils';
import {
	checkElementExpandInEditor,
	scrollToComment,
	calculateElementOffset,
} from '@confluence/comments-util';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker';
import { fg } from '@confluence/feature-gating';

import type { CommentHighlight } from './commentContextTypes';

export type CreateInlineCommentHighlight = CommentHighlight;

export type HighlightOrigin = 'userHighlight' | 'nudgeHighlight';

type NavigateToInlineCommentProps = {
	action: 'next' | 'previous';
	triggeredByKeyboardShortcut: boolean;
	currentCommentIndex: number;
	onNavigationClick: (nextMarkerRef: string) => void;
	isEditor?: boolean;
	removeCommentQueryParams: () => void;
	unresolvedInlineComments: string[];
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	contentId?: string;
	isNavigateToNextUnread?: boolean;
	unreadInlineComments?: string[];
	unreadGeneralComments?: string[];
	getCommentIndex: (commentId: string) => number;
	footerComments?: string[];
	scrollToFooterComment?: (threadKey: string) => void;
	commentsDataMap?: CommentsDataMap;
};

export type ResolvedComment = {
	commentId: string;
	inlineMarkerRef: string;
};

type InlineCommentsContextValue = {
	newCommentHighlight?: CreateInlineCommentHighlight;
	highlightOrigin: HighlightOrigin;
	activeHighlight: string | null;
	firstHighlightClicked: boolean;
	shouldDisplaySidebar: boolean;
	resolvedComment: ResolvedComment | null;
};

type InlineCommentsDispatchContextValue = {
	createComment: (newHighlight: CreateInlineCommentHighlight, origin?: HighlightOrigin) => void;
	clearCreateHighlight: () => void;
	onHighlightClick: (markerRef: string | null) => void;
	toggleSidebar: (shouldDisplay: boolean) => void;
	setResolvedComment: React.Dispatch<React.SetStateAction<ResolvedComment | null>>;
	setActiveHighlight: React.Dispatch<React.SetStateAction<string | null>>;
	navigateToInlineComment: ({
		action,
		triggeredByKeyboardShortcut,
		currentCommentIndex,
		onNavigationClick,
		getCommentIndex,
		isEditor,
		removeCommentQueryParams,
		unresolvedInlineComments,
		createAnalyticsEvent,
		contentId,
		isNavigateToNextUnread,
		unreadInlineComments,
		commentsDataMap,
	}: NavigateToInlineCommentProps) => void;
};

export const InlineCommentsDispatchContext = createContext<InlineCommentsDispatchContextValue>({
	createComment: () => {},
	clearCreateHighlight: () => {},
	onHighlightClick: () => {},
	toggleSidebar: () => {},
	setResolvedComment: () => {},
	setActiveHighlight: () => {},
	navigateToInlineComment: () => {},
});
InlineCommentsDispatchContext.displayName = 'InlineCommentsDispatchContext';

export const InlineCommentsContext = createContext<InlineCommentsContextValue>({
	newCommentHighlight: undefined,
	highlightOrigin: 'userHighlight',
	activeHighlight: null,
	firstHighlightClicked: false,
	shouldDisplaySidebar: false,
	resolvedComment: null,
});
InlineCommentsContext.displayName = 'InlineCommentsContext';

type InlineCommentsProviderProps = {
	children: ReactNode;
};

export const InlineCommentsProvider: FC<InlineCommentsProviderProps> = ({ children }) => {
	const [newCommentHighlight, setNewCommentHighlight] = useState<CreateInlineCommentHighlight>();

	const [highlightOrigin, setHighlightOrigin] = useState<HighlightOrigin>('userHighlight');

	/**
	 * We eventually want this value to be the official state for loading/displaying
	 * the UI for inline comments. InlineCommentSidebar and some child components have created
	 * stateful logic (active highlight, isSidebarShown, etc) that assumes
	 * the code is always loaded which we will eventually adjust.
	 */
	const [shouldDisplaySidebar, setShouldDisplaySidebar] = useState<boolean>(false);

	const firstHighlightClicked = useRef(false);

	const [activeHighlight, setActiveHighlight] = useState<string | null>(null);

	const [resolvedComment, setResolvedComment] = useState<ResolvedComment | null>(null);

	const providerValue = useMemo(
		() => ({
			newCommentHighlight,
			highlightOrigin,
			activeHighlight,
			resolvedComment,
			firstHighlightClicked: firstHighlightClicked.current,
			shouldDisplaySidebar,
		}),
		[
			activeHighlight,
			highlightOrigin,
			newCommentHighlight,
			resolvedComment,
			shouldDisplaySidebar,
			firstHighlightClicked,
		],
	);

	const dispatchProviderValue = useMemo(
		() => ({
			createComment: (
				newHighlight: CreateInlineCommentHighlight,
				origin: HighlightOrigin = 'userHighlight',
			) => {
				/**
				 * Creating a comment is one of the three user explicit scenarios
				 * where we want to load inline comments code.
				 */
				setShouldDisplaySidebar(true);
				setNewCommentHighlight(newHighlight);
				setHighlightOrigin(origin);
				if (resolvedComment) {
					setResolvedComment(null);
				}
			},
			clearCreateHighlight: () => setNewCommentHighlight(undefined),
			onHighlightClick: (markerRef: string | null) => {
				if (!firstHighlightClicked.current) {
					firstHighlightClicked.current = true;
				}

				/**
				 * Clicking on a highlight is one of the three user explicit scenarios
				 * where we want to load inline comments code.
				 */
				if (markerRef) {
					setShouldDisplaySidebar(true);
				} else {
					setShouldDisplaySidebar(false);
				}

				setActiveHighlight(markerRef);
			},
			setResolvedComment,
			toggleSidebar: (shouldDisplay: boolean) => setShouldDisplaySidebar(shouldDisplay),
			setActiveHighlight,
			navigateToInlineComment: ({
				action,
				triggeredByKeyboardShortcut,
				currentCommentIndex,
				onNavigationClick,
				isEditor,
				removeCommentQueryParams,
				unresolvedInlineComments,
				createAnalyticsEvent,
				contentId,
				isNavigateToNextUnread,
				unreadInlineComments = [],
				unreadGeneralComments = [],
				getCommentIndex,
				footerComments = [],
				scrollToFooterComment,
				commentsDataMap,
			}: NavigateToInlineCommentProps) => {
				if (fg('confluence_comments_select_comment_experience')) {
					selectInlineCommentCompoundExperience.addCommonAttributes({ navigationAction: action });
				}

				const isNavigatingFooterComments =
					triggeredByKeyboardShortcut &&
					footerComments?.length > 0 &&
					fg('confluence_frontend_footer_kb_nav');

				if (unresolvedInlineComments?.length < 2 && !isNavigatingFooterComments) {
					if (fg('confluence_comments_select_comment_experience')) {
						selectInlineCommentCompoundExperience.selectAnnotation.abort({
							abortReason: 'Comment navigation when only one comment',
						});
					}

					return;
				}

				// Clear the focusedCommentId queryParams so that reply does not opens up if any...
				removeCommentQueryParams();

				const combinedInlineFooterComments = [...unresolvedInlineComments, ...footerComments];
				let nextThreadKey = '';
				let currentThreadKey = '';
				if (triggeredByKeyboardShortcut && fg('confluence_frontend_footer_kb_nav')) {
					if (isNavigateToNextUnread) {
						nextThreadKey =
							getNextUnreadThreadKey(currentCommentIndex, combinedInlineFooterComments, [
								...unreadInlineComments,
								...unreadGeneralComments,
							]) ?? '';
					} else {
						nextThreadKey =
							getNextMarkerRef(action, currentCommentIndex, combinedInlineFooterComments) ?? '';
					}
					currentThreadKey = combinedInlineFooterComments[currentCommentIndex];
				} else {
					nextThreadKey =
						getNextMarkerRef(action, currentCommentIndex, unresolvedInlineComments) ?? '';
					currentThreadKey = unresolvedInlineComments[currentCommentIndex];
				}

				if (!nextThreadKey) {
					if (fg('confluence_comments_select_comment_experience')) {
						selectInlineCommentCompoundExperience.selectAnnotation.debug({
							error: new Error(`Couldn't find next annotation to select in document`),
							createAnalyticsEvent,
						});
					}
					return;
				}

				if (fg('confluence_comments_select_comment_experience')) {
					selectInlineCommentCompoundExperience.addCommonAttributes({
						annotationId: nextThreadKey,
					});
				}

				if (triggeredByKeyboardShortcut && fg('confluence_frontend_footer_kb_nav')) {
					if (
						footerComments.includes(nextThreadKey) &&
						scrollToFooterComment &&
						commentsDataMap?.general?.[nextThreadKey]
					) {
						scrollToFooterComment(nextThreadKey);
						return;
					}
				}

				const nextCommentElement =
					document.getElementById(nextThreadKey) ||
					(fg('confluence_frontend_footer_kb_nav')
						? document.getElementById(`footer-comment-thread-${nextThreadKey}-container`)
						: null);
				const previousCommentElement =
					document.getElementById(currentThreadKey) ||
					(fg('confluence_frontend_footer_kb_nav')
						? document.getElementById(`footer-comment-thread-${currentThreadKey}-container`)
						: null);

				if (nextCommentElement) {
					checkElementExpandInEditor(nextCommentElement);
				} else {
					if (fg('confluence_comments_select_comment_experience')) {
						selectInlineCommentCompoundExperience.selectAnnotation.debug({
							error: new Error('No annotation found in doc to check if in expand'),
							createAnalyticsEvent,
						});
					}
				}

				onNavigationClick(nextThreadKey);

				const nextCommentIndex = getCommentIndex(nextThreadKey);

				let heightOfMediaCommentSelectedPlaceholder = 0;

				if (fg('confluence_frontend_new_comments_scrolling')) {
					const isCurrentCommentContentMediaComment =
						previousCommentElement?.closest('.media-single-node');
					const isNavigatingToLastCommentFromPreviousAction =
						action === 'previous' && nextCommentIndex === unresolvedInlineComments.length - 1;
					const isNavigatingToFirstCommentFromNextAction =
						action === 'next' && nextCommentIndex === 0;

					if (
						isCurrentCommentContentMediaComment &&
						(action === 'next' ||
							isNavigatingToLastCommentFromPreviousAction ||
							isNavigatingToFirstCommentFromNextAction)
					) {
						// if the media comment placeholder is visible, it pushes the contents of the page down, which affects navigating to the prev and next comment
						// this means we'll need to scroll a bit less so that the cursor is still over the navigation arrow iconp
						const mediaCommentSelectedPlaceholder = document.querySelector(
							'[data-id="caption-placeholder"]',
						) as HTMLElement;
						heightOfMediaCommentSelectedPlaceholder = mediaCommentSelectedPlaceholder
							? mediaCommentSelectedPlaceholder.offsetHeight
							: 0;
					}
				}

				const previousOffset = previousCommentElement
					? calculateElementOffset(previousCommentElement)
					: 0;

				const offset = calculateElementOffset(nextCommentElement);

				const scrollableContainerSelector = isEditor
					? '.fabric-editor-popup-scroll-parent'
					: '#content-body';

				const scrollableContainer = document.querySelector(scrollableContainerSelector);

				const differenceOffset = offset - previousOffset - heightOfMediaCommentSelectedPlaceholder;
				scrollableContainer?.scrollBy(0, differenceOffset);

				scrollToComment(nextCommentElement, previousCommentElement, true, Boolean(isEditor));

				if (nextCommentElement) {
					// As we navigate inline comments with arrow icons, we want to update the cursor location to where the open inline comment is
					// unless it is a media node in which case, we want our selection to be the media node element.
					const media = nextCommentElement.querySelector(
						`.${MediaSharedClassNames.MEDIA_CONTAINER}`,
					);
					if (media) {
						if (fg('confluence_comments_select_comment_experience')) {
							selectInlineCommentCompoundExperience.addCommonAttributes({
								commentType: 'block',
								blockType: 'media',
							});
						}
						window.getSelection()?.setPosition(media);
					} else {
						if (fg('confluence_comments_select_comment_experience')) {
							selectInlineCommentCompoundExperience.addCommonAttributes({ commentType: 'inline' });
						}
						window.getSelection()?.setPosition(nextCommentElement);
					}
				}

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'navigate',
						actionSubjectId: 'inlineCommentArrowsNavigate',
						attributes: {
							triggeredByKeyboardShortcut,
							action,
						},
						objectType: 'inlineComment',
						objectId: contentId,
						source: isEditor ? 'editPageScreen' : 'renderPageScreen',
					},
				}).fire();
			},
		}),
		[
			resolvedComment,
			firstHighlightClicked,
			setShouldDisplaySidebar,
			setHighlightOrigin,
			setResolvedComment,
			setActiveHighlight,
		],
	);

	return (
		<InlineCommentsContext.Provider value={providerValue}>
			<InlineCommentsDispatchContext.Provider value={dispatchProviderValue}>
				{children}
			</InlineCommentsDispatchContext.Provider>
		</InlineCommentsContext.Provider>
	);
};

export const useInlineCommentsContext = () => {
	return useContext(InlineCommentsContext);
};

export const useInlineCommentsDispatchContext = () => {
	return useContext(InlineCommentsDispatchContext);
};
